import { SshTransportProtocol, type HostKeyVerifier } from './protocol/transport-protocol.js';
import { SshAuthProtocol } from './protocol/auth-protocol.js';
import { Connection, type ChannelStream, type SubsystemStream } from './connection.js';
import type {
	SshConnectionConfiguration,
	DuplexStream,
	ChannelOptions,
	SubsystemOptions,
} from './types.js';
import { Disposable, type Logger, consoleLogger, noopLogger } from '@ts-ssh2/common';
import { SftpClient } from './sftp/sftp-client.js';
import { KeepaliveManager } from './keepalive.js';

/**
 * Options for creating an SSH client.
 */
export type ClientOptions = {
	/** Custom host key verification callback. Defaults to accepting all keys. */
	hostKeyVerifier?: HostKeyVerifier;
	/** Enable debug logging. Defaults to false. */
	debug?: boolean;
	/** Custom logger implementation. Defaults to console.debug when debug is true. */
	logger?: Logger;
};

/**
 * SSH client.
 *
 * Orchestrates transport setup, key exchange, authentication, and provides
 * exec/shell access through a Connection instance.
 *
 * Usage:
 *   const client = new Client();
 *   await client.connect({
 *     transport: new TcpTransport(),
 *     hostname: 'example.com',
 *     user: 'admin',
 *     privateKey: fs.readFileSync('~/.ssh/id_ed25519', 'utf8'),
 *   });
 *
 *   const result = await client.exec('ls -la');
 *   const text = await new Response(result.stdout).text();
 *   console.log(text);
 *
 *   client.dispose();
 */
export class Client extends Disposable {
	private transport: SshTransportProtocol | null = null;
	private connection: Connection | null = null;
	private hostKeyVerifier: HostKeyVerifier;
	private logger: Logger;
	private keepalive: KeepaliveManager | null = null;

	constructor(options?: ClientOptions) {
		super();
		this.hostKeyVerifier = options?.hostKeyVerifier ?? (() => true);
		this.logger = options?.debug ? (options.logger ?? consoleLogger) : noopLogger;
	}

	/**
	 * Connect to an SSH server.
	 *
	 * @param config Connection configuration (transport + credentials, or existing stream).
	 * @param signal Abort signal to cancel the connection attempt.
	 */
	async connect(config: SshConnectionConfiguration, signal?: AbortSignal): Promise<void> {
		// get the duplex stream — either from a transport or an existing stream
		let stream: DuplexStream;
		if ('stream' in config) {
			this.logger.debug('using pre-existing stream');
			stream = config.stream;
		} else {
			this.logger.debug(`connecting to ${config.hostname}:${config.port ?? 22}`);
			stream = await config.transport.connect(
				{
					hostname: config.hostname,
					port: config.port ?? 22,
				},
				signal,
			);
		}

		// create transport protocol
		this.transport = new SshTransportProtocol(this.hostKeyVerifier, this.logger);

		// start the transport in the background (it runs its own read loop)
		const transportReady = new Promise<void>((resolve, reject) => {
			const readySub = this.transport!.onReady.subscribe(() => {
				readySub.dispose();
				disconnectSub.dispose();
				resolve();
			});
			const disconnectSub = this.transport!.onDisconnect.subscribe((evt) => {
				readySub.dispose();
				disconnectSub.dispose();
				reject(new Error(`Disconnected during handshake: ${evt.description}`));
			});
		});

		// run the transport (background — don't await, it runs until disconnect)
		this.transport.run(stream, signal).catch(() => { });

		// wait for the handshake (version exchange + kex + newkeys)
		await transportReady;

		// authenticate
		const auth = new SshAuthProtocol(
			this.transport,
			{
				username: config.user,
				password: config.password,
				privateKey: config.privateKey,
				passphrase: config.passphrase,
			},
			this.logger,
		);

		try {
			await auth.authenticate();
		} finally {
			auth.dispose();
		}

		this.logger.debug('connected');

		// create connection (channel manager)
		this.connection = new Connection(this.transport, this.logger);

		// start keepalive (enabled by default at 10s interval)
		const interval = config.keepaliveInterval ?? 10_000;
		if (interval > 0) {
			this.keepalive = new KeepaliveManager(
				this.transport,
				this.connection,
				{ intervalMs: interval, timeoutMs: config.keepaliveTimeout ?? 15_000 },
				this.logger,
			);
			this.keepalive.start();
		}
	}

	/**
	 * Execute a command on the remote server.
	 */
	async exec(command: string, options?: ChannelOptions): Promise<ChannelStream> {
		if (!this.connection) {
			throw new Error('Not connected');
		}
		return this.connection.exec(command, options);
	}

	/**
	 * Open an interactive shell on the remote server.
	 */
	async shell(options?: ChannelOptions): Promise<ChannelStream> {
		if (!this.connection) {
			throw new Error('Not connected');
		}
		return this.connection.shell(options);
	}

	/**
	 * Open a subsystem on the remote server (e.g. "sftp").
	 */
	async subsystem(name: string, options?: SubsystemOptions): Promise<SubsystemStream> {
		if (!this.connection) {
			throw new Error('Not connected');
		}
		return this.connection.subsystem(name, options);
	}

	/**
	 * Open an SFTP session.
	 *
	 * Opens the "sftp" subsystem and performs the SFTP protocol handshake,
	 * returning a ready-to-use SftpClient instance.
	 */
	async sftp(): Promise<SftpClient> {
		if (!this.connection) {
			throw new Error('Not connected');
		}
		const stream = await this.connection.subsystem('sftp');
		return SftpClient.create(stream, this.logger);
	}

	/**
	 * Open a TCP forwarding channel to a remote host:port.
	 *
	 * The returned DuplexStream can be passed as a pre-existing stream
	 * to a second Client.connect() call for connection hopping.
	 */
	async forwardOut(
		destHost: string,
		destPort: number,
		signal?: AbortSignal,
	): Promise<DuplexStream> {
		if (!this.connection) {
			throw new Error('Not connected');
		}
		return this.connection.forwardOut(destHost, destPort, signal);
	}

	/**
	 * Disconnect from the server and release all resources.
	 */
	override dispose(): void {
		this.keepalive?.dispose();
		this.connection?.dispose();
		this.transport?.dispose();
		this.keepalive = null;
		this.connection = null;
		this.transport = null;
		super.dispose();
	}
}
